import { useLoading } from "helpers/custom_hooks"
import React, { useState } from "react"

import { Alert, Button, Col, Row, TabPane } from "reactstrap"
//Import Pricing Cards
import CardPricing from "../../../components/Common/card-pricing"
import AddonPackagesDropdown from "../../../components/Common/AddonPackagesDropdown"
import { UpdateBilling } from "helpers/backend_helper"
import { displayMessage } from "helpers/misc_helpers"

const PricingTab = ({ nextTab, company, businessType }) => {
  const setLoading = useLoading()
  const [error, setError] = useState()
  const [anual, setAnual] = useState(true)

  const handleSave = async accountType => {
    try {
      setLoading(true)

      const reqObj = {
        accountType: accountType,
        billingPeriod: anual ? "anual" : "half-yearly",
      }

      await UpdateBilling(reqObj)

      nextTab()
    } catch (error) {
      if (error?.response?.status === 400)
        displayMessage(error.response.data, setError)
      else displayMessage("An unexpected error happened!", setError)
      console.error(error)
    }
    setLoading(false)
  }

  const duration = anual ? "per year" : "per half year"

  // ── OEM/EMS: Hauptlinie (Free / Basic / Partner Cockpit) ────────────────────
  const freePackage = {
    id: 1,
    title: "Free Package",
    description: " ",
    description2: "Access to our part search and watchlist.",
    price: anual ? "0" : "0",
    duration,
    link: _ => handleSave("Standard"),
    btnText: "Choose Plan",
    userType: "Free",
    features: [
      { title: "Unlimited chipsconnect search", icon: "bx bx-check", color: "primary" },
      { title: "50 advanced (external) searches / month", icon: "bx bx-check", color: "primary" },
      {
        title: "Upload your Excess material list and Watchlist",
        icon: "bx bx-check",
        color: "primary",
      },
      { title: "Single User Account", icon: "bx bx-check", color: "primary" },
      { title: "Buy / Sell parts", icon: "bx bx-x", color: "danger" },
    ],
  }
  const basicPackage = {
    id: 2,
    title: "Basic Package",
    description: "Pricing plan for basic features",
    description2:
      "Unlimited access to all basic features and no user restrictions",
    price: anual ? "500" : "250",
    duration,
    link: _ => handleSave("Premium"),
    btnText: "Choose Plan",
    userType: "Standard",
    features: [
      { title: "Unlimited part search", icon: "bx bx-check", color: "primary" },
      { title: "Buy / Sell parts", icon: "bx bx-check", color: "primary" },
      { title: "Unlimited user", icon: "bx bx-check", color: "primary" },
      { title: "API interface", icon: "bx bx-x", color: "danger" },
    ],
  }
  const partnerCockpitPackage = {
    id: 6,
    title: "Partner Cockpit",
    description: "Collaborative partner management platform",
    description2: "Available as add-on after registration.",
    price: anual ? "2000" : "1000",
    duration,
    // Kein Self-Service in der Registrierung — nach Registrierung anfragbar.
    features: [
      { title: "Partner relationship management", icon: "bx bx-check", color: "primary" },
      { title: "Collaborative workspace", icon: "bx bx-check", color: "primary" },
      { title: "Document sharing and management", icon: "bx bx-check", color: "primary" },
      { title: "Basic package required", icon: "bx bx-x", color: "danger" },
    ],
  }

  // ── OEM/EMS: Add-on-Pulldown (API / QC) ─────────────────────────────────────
  const apiPackage = {
    id: 3,
    title: "Add-on API Package",
    description: "Pricing plan to get the API",
    description2: "No one-time costs for API implementation. (Basic Package required)",
    price: anual ? "300" : "150",
    duration,
    features: [
      { title: "API interface", icon: "bx bx-check", color: "primary" },
      { title: "API maintenance", icon: "bx bx-check", color: "primary" },
      { title: "Automatic updates", icon: "bx bx-check", color: "primary" },
      { title: "Basic package required", icon: "bx bx-x", color: "danger" },
    ],
  }
  const qcPackage = {
    id: 4,
    title: "Add-on QC-Module",
    description: "Standardized QC Test workflow",
    description2: "Ensures products meet set standards.",
    price: anual ? "500" : "250",
    duration,
    features: [
      { title: "Standardized QC Test workflow", icon: "bx bx-check", color: "primary" },
      { title: "One-stop request management", icon: "bx bx-check", color: "primary" },
      { title: "Peer-to-peer Buyer-Seller-QC lab", icon: "bx bx-check", color: "primary" },
      { title: "Automatic updates", icon: "bx bx-check", color: "primary" },
    ],
  }

  const oemMainLine = [freePackage, basicPackage, partnerCockpitPackage]
  const oemAddons = [apiPackage, qcPackage]

  // ── Trader: keine Preise; Free/Basic/API/QC nur zur Info im Pulldown ────────
  const stripPrice = card => ({ ...card, price: null, link: null, btnText: undefined })
  const traderFreeInfo = {
    id: "trader-free",
    title: "Free Package",
    description: " ",
    description2: "Base access for traders.",
    price: null,
    features: [
      { title: "Anonymous display of your searches", icon: "bx bx-check", color: "primary" },
      { title: "Upload parts / excess list", icon: "bx bx-check", color: "primary" },
      { title: "Single User Account", icon: "bx bx-check", color: "primary" },
      { title: "Buy / Sell parts", icon: "bx bx-x", color: "danger" },
    ],
  }
  const traderPackages = [
    traderFreeInfo,
    stripPrice(basicPackage),
    stripPrice(apiPackage),
    stripPrice(qcPackage),
  ]

  return (
    <TabPane tabId={6}>
      {error && (
        <Alert color="danger" style={{ marginTop: "13px" }}>
          {error}
        </Alert>
      )}
      <div>
        <Row className="justify-content-center mt-5">
          <Col lg={6}>
            <div className="text-center mb-3">
              <h4>Choose your Pricing package</h4>
              <p className="text-muted">Please select</p>
            </div>
          </Col>
        </Row>

        <Row className={businessType === "Trader" ? "d-none" : ""}>
          <Col className="text-center mb-3">
            <div className="btn-group" role="group">
              <input
                type="radio"
                className="btn-check"
                name="btnradio"
                id="btnradio4"
                autoComplete="off"
                defaultChecked
                onChange={e => setAnual(true)}
              />
              <label className="btn btn-outline-secondary" htmlFor="btnradio4">
                Anually
              </label>

              <input
                type="radio"
                className="btn-check"
                name="btnradio"
                id="btnradio5"
                autoComplete="off"
                onChange={e => setAnual(false)}
              />
              <label className="btn btn-outline-secondary" htmlFor="btnradio5">
                Semi-Anually
              </label>
            </div>
          </Col>
        </Row>
        {businessType === "Trader" ? (
          <>
            <Row className="justify-content-center">
              <Col lg={8}>
                <Alert color="info">
                  <i className="bx bx-info-circle me-1" />
                  As a trader, your conditions are agreed individually after
                  registration and an initial call with chipsconnect. The
                  packages below are shown for information only.
                </Alert>
                <AddonPackagesDropdown
                  addons={traderPackages}
                  title="Packages"
                  defaultOpen={true}
                />
                <div className="text-center mt-4">
                  <Button color="primary" onClick={() => handleSave("Trader")}>
                    Continue <i className="bx bx-right-arrow-alt ms-1" />
                  </Button>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row className="justify-content-center">
              {oemMainLine.map((pricing, key) => (
                <CardPricing pricing={pricing} key={"_pricing_" + key} />
              ))}
            </Row>
            <Row className="justify-content-center">
              <Col lg={10}>
                <AddonPackagesDropdown addons={oemAddons} />
              </Col>
            </Row>
          </>
        )}
      </div>
    </TabPane>
  )
}

export default PricingTab
